import React, { memo, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { BRAND_COLOR, getAppColors, GRAY3_COLOR } from 'lib/constants'
import { CSS_COLOR_NAMES, NamedColor } from 'lib/colors/cssColorNames'
import { LANGUAGES } from 'lib/types/languages'

const DEFAULT_LANGUAGE = 'en'

function Swatch({ hex }: { hex?: string }) {
  return (
    <Box
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        border: '1px solid rgba(0,0,0,0.15)',
        background: hex || 'transparent',
      }}
    />
  )
}

export type AddDeviceGroupResult = {
  language: string
  color: string | null
}

const StyledFormControl = styled(FormControl)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  borderColor: BRAND_COLOR,
  textAlign: 'center',
  width: '100%',
})

type Props = {
  open: boolean
  channelName?: string | null
  busy?: boolean
  onClose: () => void
  onSubmit: (result: AddDeviceGroupResult) => void | Promise<void>
}

function AddDeviceGroupModal({ open, channelName, busy = false, onClose, onSubmit }: Props) {
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE)
  const [color, setColor] = useState<string>('')

  useEffect(() => {
    if (!open) return
    setLanguage(DEFAULT_LANGUAGE)
    setColor('')
  }, [open])

  const languageOptions = useMemo(() => LANGUAGES, [])

  const title = channelName ? `Add Language — ${channelName}` : 'Add Language'

  const submitDisabled = busy

  async function handleSubmit() {
    await onSubmit({
      language,
      color: color ? color : null,
    })
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs">
      <DialogTitle style={{ fontFamily: 'Montserrat', fontWeight: 900 }}>{title}</DialogTitle>

      <DialogContent dividers>
        <div style={{ marginBottom: 10, color: getAppColors('--color-secondary'), fontSize: 13 }}>
          {'Choose a '}
          <span style={{ fontWeight: 900 }}>{'language'}</span>
          {' and an optional '}
          <span style={{ fontWeight: 900 }}>{'color'}</span>
          {'.'}
        </div>

        <div
          style={{
            border: `1px solid ${getAppColors('--color-gray-4')}`,
            borderRadius: 10,
            overflow: 'hidden',
            padding: 14,
          }}
        >
          <div style={{ marginBottom: 14 }}>
            <Typography style={{ fontWeight: 900, marginBottom: 6 }}>{'Language'}</Typography>

            <StyledFormControl>
              <Select
                value={language}
                onChange={e => setLanguage(String(e.target.value))}
                IconComponent={KeyboardArrowDownIcon}
                disableUnderline
                disabled={busy}
                MenuProps={{
                  // `getContentAnchorEl: null` is what lets `anchorOrigin` apply at
                  // all: while a content anchor exists, MUI v4 aligns the *selected*
                  // item over the trigger and discards `anchorOrigin.vertical`. With
                  // 59 languages that puts the paper hundreds of pixels from the
                  // field — and somewhere different for every selection — so the
                  // menu appeared to jump around the dialog. Matches the pattern in
                  // VoicePTT, DeviceDetailDrawer, NotificationCenter and HoverBox.
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: { style: { maxHeight: 320 } },
                }}
              >
                {languageOptions.map(l => (
                  <MenuItem key={l.code} value={l.code}>
                    {`${l.label} — `}
                    <span style={{ textTransform: 'none' }}>{l.code}</span>
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </div>

          <div>
            <Typography style={{ fontWeight: 900, marginBottom: 6 }}>{'Color'}</Typography>

            <StyledFormControl>
              <Select
                value={color}
                onChange={e => setColor(String(e.target.value))}
                IconComponent={KeyboardArrowDownIcon}
                disableUnderline
                displayEmpty
                disabled={busy}
                MenuProps={{
                  // `getContentAnchorEl: null` is what lets `anchorOrigin` apply at
                  // all: while a content anchor exists, MUI v4 aligns the *selected*
                  // item over the trigger and discards `anchorOrigin.vertical`. With
                  // 59 languages that puts the paper hundreds of pixels from the
                  // field — and somewhere different for every selection — so the
                  // menu appeared to jump around the dialog. Matches the pattern in
                  // VoicePTT, DeviceDetailDrawer, NotificationCenter and HoverBox.
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: { style: { maxHeight: 320 } },
                }}
                renderValue={(val: any) => {
                  const v = String(val || '')
                  if (!v) return <em style={{ color: getAppColors('--color-secondary') }}>Select Color</em>
                  return (
                    <Box display="flex" alignItems="center" style={{ gap: 10, justifyContent: 'center' }}>
                      <Swatch hex={v} />
                      <span style={{ fontWeight: 800 }}>{v}</span>
                    </Box>
                  )
                }}
              >
                <MenuItem value="">
                  <em>Default (no color)</em>
                </MenuItem>

                {CSS_COLOR_NAMES.map((c: NamedColor) => (
                  <MenuItem key={c.hex} value={c.hex}>
                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                      <Swatch hex={c.hex} />
                      <span>{c.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </div>
        </div>
      </DialogContent>

      <DialogActions style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleSubmit}
          style={{
            backgroundColor: submitDisabled ? undefined : BRAND_COLOR,
            color: submitDisabled ? undefined : '#fff',
            fontWeight: 900,
            textTransform: 'none',
            borderRadius: 18,
            height: 34,
            paddingLeft: 16,
            paddingRight: 16,
            minWidth: 140,
          }}
        >
          {busy ? <CircularProgress size={16} thickness={5} /> : 'Add Language'}
        </Button>

        <Button
          onClick={() => !busy && onClose()}
          variant="outlined"
          disabled={busy}
          style={{
            borderColor: BRAND_COLOR,
            color: BRAND_COLOR,
            fontWeight: 900,
            textTransform: 'none',
            borderRadius: 18,
            height: 34,
            paddingLeft: 14,
            paddingRight: 14,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(AddDeviceGroupModal)
